// Global Instructions Rule Applied!

import React, { useMemo } from 'react'
import { Row, Col, Spin } from 'antd'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { FieldTypeEnum, type FieldConfig } from '../../../../../core/components/InputManager/types.ts'
import { useFundInvestorsSummary } from '../../../hooks/useFundInvestorsSummary.ts'
import FundInvestorsTable from '../../FundInvestorsTable.tsx'

interface InvestorsTabProps {
  fundId: string | null
}

const txt = (v: unknown): string => (v == null || v === '' ? '–' : String(v))

const readOnlyText = (key: string, label: string, value: unknown): FieldConfig =>
  ({
    key,
    label,
    type: FieldTypeEnum.TEXT,
    value: txt(value),
    editable: false
  }) as FieldConfig

export function InvestorsTab({ fundId }: InvestorsTabProps): React.ReactElement {
  const { data, loading, refetch: refetchSummary } = useFundInvestorsSummary(fundId)
  const d = data ?? {}

  const summaryFields: FieldConfig[] = useMemo(
    () => [
      readOnlyText('funds__committed_capital', 'Committed Capital', d.funds__committed_capital_fmt),
      readOnlyText('no_investors', 'No. of Investors', d.no_investors_fmt)
    ],
    [d.funds__committed_capital_fmt, d.no_investors_fmt]
  )

  if (loading && !data) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <Spin size='large' />
      </div>
    )
  }

  return (
    <div className='funds-investors flex flex-col h-full p-3 gap-3 overflow-hidden'>
      {/* Inline-edit value boxes match the muted label colour (field labels
          now default to rgba(0,0,0,0.45) in InputManager). */}
      <style>{`
        .funds-investors .inline-edit-label {
          color: rgba(0, 0, 0, 0.45) !important;
          font-weight: 400 !important;
        }
      `}</style>

      <FormSection title='Summary' titleColor='blue' scrollable={false}>
        <div className='p-3'>
          <Row gutter={24}>
            <Col xs={24} md={8}>
              <InputManager inputMode='read' labelPosition='inline' fields={summaryFields} fieldWrapping={true} />
            </Col>
          </Row>
        </div>
      </FormSection>

      <div className='flex-1 min-h-0 flex flex-col'>
        <FundInvestorsTable fundId={fundId} onCommitmentsChanged={refetchSummary} />
      </div>
    </div>
  )
}

export default InvestorsTab
